'use client';

import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Brain, MapPin, Users, Award } from 'lucide-react';

const cutoffTrendData = [
  { year: '2022', AIIMS: 45, MAMC: 295, KEM: 1050, CMC: 1100 },
  { year: '2023', AIIMS: 48, MAMC: 305, KEM: 1080, CMC: 1150 },
  { year: '2024', AIIMS: 50, MAMC: 312, KEM: 1100, CMC: 1200 },
  { year: '2025', AIIMS: 52, MAMC: 320, KEM: 1120, CMC: 1250 },
  { year: '2026*', AIIMS: 54, MAMC: 330, KEM: 1140, CMC: 1300 },
];

const stateDemanData = [
  { state: 'Delhi', demand: 95, seats: 1850 },
  { state: 'Maharashtra', demand: 88, seats: 4200 },
  { state: 'Karnataka', demand: 82, seats: 3800 },
  { state: 'Tamil Nadu', demand: 79, seats: 5100 },
  { state: 'UP', demand: 71, seats: 3200 },
  { state: 'Rajasthan', demand: 68, seats: 2800 },
  { state: 'Kerala', demand: 65, seats: 2100 },
  { state: 'Gujarat', demand: 62, seats: 2400 },
];

const seatDistribution = [
  { name: 'Government', value: 42, color: '#0891b2' },
  { name: 'Private', value: 31, color: '#7c3aed' },
  { name: 'Deemed', value: 18, color: '#d97706' },
  { name: 'Central', value: 9, color: '#059669' },
];

const topCompetitiveColleges = [
  { college: 'AIIMS New Delhi', closingRank: 50, change: -2, state: 'Delhi' },
  { college: 'MAMC Delhi', closingRank: 320, change: +8, state: 'Delhi' },
  { college: 'VMMC Delhi', closingRank: 680, change: +12, state: 'Delhi' },
  { college: 'CMC Vellore', closingRank: 1200, change: +50, state: 'Tamil Nadu' },
  { college: 'AFMC Pune', closingRank: 430, change: +5, state: 'Maharashtra' },
];

const COLORS = ['#7c3aed', '#0891b2', '#059669', '#d97706'];

export default function AdmissionIntelligenceSection() {
  const [activeChart, setActiveChart] = useState<'cutoff' | 'state' | 'seats'>('cutoff');

  return (
    <section className="py-24 px-6 lg:px-8 relative overflow-hidden">
      <div className="floating-orb w-96 h-96 bg-accent/6 bottom-0 left-0 animate-float" />
      <div className="max-w-screen-xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 glass-card rounded-full text-xs text-[#1a3a8f] border border-[#1a3a8f]/15 mb-4 font-semibold">
            <Brain size={12} />
            <span>AI-Powered Admission Analytics</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-4 tracking-tight">
            Admission <span className="gradient-text">Intelligence</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Real-time cutoff trends, state-wise demand analysis, and seat distribution insights — data your counsellor uses to build your winning strategy.
          </p>
        </div>

        {/* Chart Tabs */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {[
            { id: 'cutoff', label: 'Cutoff Trends 2022–2026', icon: TrendingUp },
            { id: 'state', label: 'State-wise Demand', icon: MapPin },
            { id: 'seats', label: 'Seat Distribution', icon: Users },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveChart(tab.id as typeof activeChart)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${activeChart === tab.id ? 'bg-primary text-white shadow-md' : 'glass-card text-muted-foreground hover:text-foreground border border-white/50'}`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Chart */}
          <div className="lg:col-span-2 glass-card-white rounded-3xl p-6 border border-white/60 shadow-sm">
            {activeChart === 'cutoff' && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-foreground">Closing Rank Trends</h3>
                    <p className="text-xs text-muted-foreground mt-1">Top colleges · General category · AIQ · MBBS</p>
                  </div>
                  <span className="text-xs px-2.5 py-1 bg-warning/10 text-warning border border-warning/20 rounded-full font-semibold">* 2026 Projected</span>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={cutoffTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" />
                    <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                    <Tooltip
                      contentStyle={{ background: 'rgba(255,255,255,0.95)', border: '1px solid rgba(15,23,42,0.1)', borderRadius: '12px', fontSize: '12px' }}
                    />
                    <Line type="monotone" dataKey="AIIMS" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 4, fill: '#7c3aed' }} />
                    <Line type="monotone" dataKey="MAMC" stroke="#0891b2" strokeWidth={2.5} dot={{ r: 4, fill: '#0891b2' }} />
                    <Line type="monotone" dataKey="KEM" stroke="#059669" strokeWidth={2.5} dot={{ r: 4, fill: '#059669' }} />
                    <Line type="monotone" dataKey="CMC" stroke="#d97706" strokeWidth={2.5} dot={{ r: 4, fill: '#d97706' }} />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-4 mt-4">
                  {[
                    { label: 'AIIMS Delhi', color: '#7c3aed' },
                    { label: 'MAMC Delhi', color: '#0891b2' },
                    { label: 'KEM Mumbai', color: '#059669' },
                    { label: 'CMC Vellore', color: '#d97706' },
                  ].map(l => (
                    <div key={l.label} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full" style={{ background: l.color }} />
                      <span className="text-xs text-muted-foreground">{l.label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {activeChart === 'state' && (
              <>
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-foreground">State-wise Demand Index</h3>
                  <p className="text-xs text-muted-foreground mt-1">Competition intensity by state (100 = highest)</p>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={stateDemanData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis dataKey="state" type="category" tick={{ fontSize: 12, fill: '#64748b' }} width={80} />
                    <Tooltip
                      contentStyle={{ background: 'rgba(255,255,255,0.95)', border: '1px solid rgba(15,23,42,0.1)', borderRadius: '12px', fontSize: '12px' }}
                      formatter={(val: number) => [`${val}/100`, 'Demand Index']}
                    />
                    <Bar dataKey="demand" fill="#7c3aed" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}

            {activeChart === 'seats' && (
              <>
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-foreground">MBBS Seat Distribution</h3>
                  <p className="text-xs text-muted-foreground mt-1">Percentage of total MBBS seats by college type</p>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-8">
                  <ResponsiveContainer width={220} height={220}>
                    <PieChart>
                      <Pie data={seatDistribution} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value">
                        {seatDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val: number) => [`${val}%`, 'Share']} contentStyle={{ background: 'rgba(255,255,255,0.95)', border: '1px solid rgba(15,23,42,0.1)', borderRadius: '12px', fontSize: '12px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-3 flex-1">
                    {seatDistribution.map(s => (
                      <div key={s.name} className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: s.color }} />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-foreground">{s.name}</span>
                            <span className="text-sm font-bold" style={{ color: s.color }}>{s.value}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${s.value}%`, background: s.color }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Most Competitive Colleges */}
          <div className="glass-card-white rounded-3xl p-6 border border-white/60 shadow-sm">
            <div className="flex items-center gap-2 mb-5">
              <Award size={18} className="text-warning" />
              <h3 className="text-base font-bold text-foreground">Most Competitive</h3>
            </div>
            <div className="space-y-3">
              {topCompetitiveColleges.map((c, i) => (
                <div key={c.college} className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{c.college}</p>
                    <p className="text-xs text-muted-foreground">{c.state} · Closing: {c.closingRank}</p>
                  </div>
                  <span className={`text-xs font-bold ${c.change > 0 ? 'text-danger' : 'text-success'}`}>
                    {c.change > 0 ? '+' : ''}{c.change}
                  </span>
                </div>
              ))}
            </div>

            {/* Quick stats */}
            <div className="mt-5 pt-5 border-t border-slate-100 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Total MBBS Seats (India)</span>
                <span className="text-sm font-bold text-foreground">1,09,145</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">NEET 2025 Qualifiers</span>
                <span className="text-sm font-bold text-foreground">13.16 Lakh</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Seat-to-Qualifier Ratio</span>
                <span className="text-sm font-bold text-danger">1 : 12</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
